import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { metricsAPI } from '../services/api';
import { loadDashboardData } from '../utils/loadDashboardData';

const CompanySelector = () => {
  const navigate = useNavigate();
  const companies = useStore((state) => state.companies);
  const currentCompany = useStore((state) => state.currentCompany);
  const setCurrentCompany = useStore((state) => state.setCurrentCompany);
  const setMetricDefinitions = useStore((state) => state.setMetricDefinitions);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCompanyChange = async (company) => {
    setIsOpen(false);

    // Skip if already on this company
    if (currentCompany?.id === company.id) {
      return;
    }

    // Set company (also sets dataReady: false to show loading state)
    setCurrentCompany(company);

    try {
      // Load dashboard and metrics in parallel via shared utility
      await loadDashboardData({ companyId: company.id });

      // Load metric definitions (secondary concern, doesn't block rendering)
      const definitionsResponse = await metricsAPI.getDefinitions(company.id);
      if (definitionsResponse.success) {
        setMetricDefinitions(definitionsResponse.data);
      }

      navigate('/');
    } catch (error) {
      console.error('Error switching company:', error);
      window.location.reload();
    }
  };

  // Don't show selector if user has no companies
  if (!companies || companies.length === 0) {
    return null;
  }

  return (
    <div className="company-selector" ref={dropdownRef}>
      <button
        className="company-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="selector-icon">🏢</span>
        <span className="company-name">
          {currentCompany?.name || 'Select Company'}
        </span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="company-dropdown">
          {companies.map((company) => (
            <button
              key={company.id}
              className={`company-option ${
                currentCompany?.id === company.id ? 'active' : ''
              }`}
              onClick={() => handleCompanyChange(company)}
            >
              <span className="company-option-name">{company.name}</span>
              {currentCompany?.id === company.id && (
                <span className="checkmark">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
